import { useTranslation } from 'react-i18next';
import { Box, Typography, useTheme, Card, Chip, TextField, InputAdornment } from '@mui/material';
import { CallReceived, CallMade, Search } from '@mui/icons-material';

import { CallStatus } from '@bandido/shared/constants';
import { useGetCalls } from '@frontend/features/calls/queries';
import { Table, usePagination } from '@frontend/features/common/components/Table';
import { useDebouncedState } from '@frontend/features/common/hooks/useDebouncedState';
import { formatISOStringToFriendlyDateWithTime } from '@frontend/features/common/helper';

const callStatusColor: Record<
  CallStatus,
  'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'
> = {
  [CallStatus.COMPLETED]: 'success',
  [CallStatus.IN_PROGRESS]: 'info',
  [CallStatus.DECLINED]: 'warning',
};

const CallTypeContainer = ({ children }: { children: React.ReactNode }) => (
  <Box display="flex" alignItems="center" gap={1}>
    {children}
  </Box>
);

export const CallsList = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [search, setSearch] = useDebouncedState('', 500);

  const pagination = usePagination();

  const { data: callsData, isLoading } = useGetCalls({
    search,
    pageSize: pagination.rowsPerPage,
    currentPage: pagination.page,
  });

  return (
    <Box display="flex" gap={4} flexDirection="column" p={theme.spacing(4)}>
      <Box>
        <Typography variant="h6" color={theme.palette.text.primary}>
          {t('common.nav.calls')}
        </Typography>
        <Typography variant="subtitle1" align="left" color={theme.palette.text.disabled}>
          {t('calls.subtitle')}
        </Typography>
      </Box>

      <Card
        sx={{
          p: theme.spacing(2),
        }}
        elevation={4}
      >
        <Box display="flex" alignItems="center" gap={theme.spacing(3)} marginBottom={theme.spacing(3)}>
          <TextField
            size="small"
            placeholder={t('common.search')}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 300 }}
          />
          <Typography
            variant="subtitle1"
            align="left"
            color={theme.palette.text.primary}
            sx={{
              flexGrow: 1,
            }}
          >
            {callsData?.count || 0} {t('common.results')}
          </Typography>
        </Box>

        {callsData?.count !== undefined && callsData.count > 0 && (
          <Table
            isLoading={isLoading}
            columns={[
              {
                id: 'isIncoming',
                label: t('calls.table.type'),
                align: 'left',
              },
              {
                id: 'from',
                label: t('calls.table.from'),
                align: 'left',
              },
              {
                id: 'to',
                label: t('calls.table.to'),
                align: 'left',
              },
              {
                id: 'agent',
                label: t('calls.table.agent'),
                align: 'left',
              },
              {
                id: 'status',
                label: t('calls.table.status'),
                align: 'right',
              },
              {
                id: 'createdAt',
                label: t('calls.table.createdAt'),
                align: 'left',
              },
            ]}
            data={{
              key: 'id',
              items: callsData.items.map((call) => ({
                ...call,
                isIncoming: call.isIncoming ? (
                  <CallTypeContainer>
                    <CallReceived color="success" />
                    <Typography variant="subtitle2">{t('calls.table.inbound')}</Typography>
                  </CallTypeContainer>
                ) : (
                  <CallTypeContainer>
                    <CallMade color="primary" />
                    <Typography variant="subtitle2">{t('calls.table.outbound')}</Typography>
                  </CallTypeContainer>
                ),
                agent: call.userId ? `${call.agentFirstName} ${call.agentLastName}` : t('calls.table.notAssigned'),
                status: call.status && (
                  <Chip
                    color={callStatusColor[call.status] || 'default'}
                    variant="filled"
                    label={t(`calls.status.${call.status}`, call.status)}
                    size="small"
                  />
                ),
                createdAt: formatISOStringToFriendlyDateWithTime(call.createdAt),
              })),
              totalCount: callsData.count,
            }}
            pagination={pagination}
            stickyHeader
          />
        )}

        {(!callsData?.count || callsData.count === 0) && !isLoading && (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
            <Typography variant="body1" color={theme.palette.text.disabled}>
              {t('common.tableList.empty')}
            </Typography>
          </Box>
        )}
      </Card>
    </Box>
  );
};
